import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAthleteMode } from "./AthleteModeContext";
import { fetchAthleteDeal, respondToOffer } from "./athleteServices";
import { EMPTY_VALUE, athleteCounterAnalysis, counterSummary, fmtMoney } from "./athleteUtils";
import { buildMilestones, fmtLongRange } from "../../ForBrands/brandDealUtils";
import {
  ActionBar,
  BackLink,
  Badge,
  Btn,
  Card,
  ErrorState,
  Field,
  Input,
  InsightBox,
  Loading,
  Overline,
  Select,
  TextArea,
} from "../../ForBrands/ui/BrandUI";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";
import CounterMobile from "./mobile/CounterMobile";
import { DealScreen } from "./mobile/OfferMobile";

// Athlete Mode · counter offer form (Figma 05 · Counter Offer Form): the
// original brand offer for reference, the negotiation form, a live summary
// of what changed, and the Cache AI counter analysis.
// On phones (inside the Athlete Mode shell, below 640px) the page renders
// CounterMobile instead (Figma 2000:967), fed the same form state.

const SCHEDULES = [
  { value: "brand", label: "Keep the brand's schedule" },
  { value: "half", label: "2 milestones (50% / 50%)" },
  { value: "milestone", label: "3 milestones (30% / 35% / 35%)" },
  { value: "upfront", label: "Paid upfront on signature" },
  { value: "on_completion", label: "Paid on completion" },
];

const scheduleMilestones = (schedule, compensation, offer) => {
  const comp = Number(compensation) || 0;
  if (schedule === "brand") {
    const total = (offer.milestones || []).reduce((s, m) => s + (Number(m.amount) || 0), 0) || comp;
    const scaled = (offer.milestones || []).map((m) => ({ ...m, amount: Math.round(comp * ((Number(m.amount) || 0) / total)) }));
    const diff = comp - scaled.reduce((s, m) => s + m.amount, 0);
    if (scaled.length) scaled[scaled.length - 1].amount += diff;
    return scaled;
  }
  if (schedule === "half") {
    const first = Math.round(comp / 2);
    return [
      { id: "m1", label: "on contract signature", amount: first, pct: 50 },
      { id: "m2", label: "on campaign completion and approval", amount: comp - first, pct: 50 },
    ];
  }
  return buildMilestones(schedule, comp);
};

const scheduleText = (milestones) =>
  milestones.length
    ? `${milestones.length} milestone${milestones.length === 1 ? "" : "s"} (${milestones.map((m) => fmtMoney(m.amount)).join(" / ")})`
    : EMPTY_VALUE;

const RefRow = ({ label, children }) => (
  <div className="flex items-center justify-between gap-4 border-b border-bm-border py-3">
    <span className="bm-body-m text-bm-muted">{label}</span>
    <span className="text-right bm-label-l text-bm-ink">{children}</span>
  </div>
);

const AthleteCounterPage = () => {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { refreshBadges } = useAthleteMode();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const phone = usePhoneTheme();
  const location = useLocation();
  const toDeals = () => navigate("/athlete/deals");

  // Phone Back, Cancel and a sent counter all return to the offer. The stack
  // screens have no bottom nav, so this must not stack a second offer on top
  // (the offer's Back would reopen this form): opened from the offer, step
  // back in history; opened any other way (a link), swap this screen for the
  // offer, and mark it `firstEntry` when there was nothing before it.
  const toOffer = () => {
    if (location?.state?.fromOffer) {
      navigate(-1);
      return;
    }
    const firstEntry = !location?.key || location.key === "default" || Boolean(location?.state?.firstEntry);
    navigate(`/athlete/deals/${dealId}/offer`, { replace: true, state: { firstEntry } });
  };

  useEffect(() => {
    fetchAthleteDeal(dealId)
      .then((payload) => {
        setData(payload);
        const base = payload.deal.counter || payload.deal.offer || {};
        setForm({
          compensation: base.compensation || "",
          deliverables: (base.deliverables || []).map((r) => ({ ...r })),
          startDate: base.startDate || "",
          endDate: base.endDate || "",
          schedule: "brand",
          exclusivityDays: base.exclusivityDays ?? payload.deal.offer?.exclusivityDays ?? 0,
          note: base.note || "",
        });
        setError(null);
      })
      .catch((err) => setError(err.message || "Could not load this offer"));
  }, [dealId]);

  const offer = data?.deal?.offer;
  const milestones = useMemo(() => (form && offer ? scheduleMilestones(form.schedule, form.compensation, offer) : []), [form, offer]);
  const summary = useMemo(() => (form && offer ? counterSummary(offer, form) : null), [form, offer]);
  const analysis = useMemo(
    () => (form && offer ? athleteCounterAnalysis({ offer, form, marketRange: data?.marketRange }) : ""),
    [form, offer, data],
  );

  if (error) {
    if (phone) {
      return (
        <DealScreen title="Counter Proposal" onBack={toDeals} backLabel="Back to My Deals">
          <ErrorState>{error}</ErrorState>
        </DealScreen>
      );
    }
    return (
      <div className="py-10">
        <ErrorState>{error}</ErrorState>
      </div>
    );
  }
  if (!data || !form) {
    if (phone) {
      return (
        <DealScreen title="Counter Proposal" onBack={toOffer} backLabel="Back to offer">
          <Loading />
        </DealScreen>
      );
    }
    return <Loading />;
  }
  if (!offer || !["offer_sent", "countered"].includes(data.deal.status)) {
    if (phone) {
      return (
        <DealScreen title="Counter Proposal" onBack={toDeals} backLabel="Back to My Deals">
          <ErrorState>This offer is no longer open for a counter.</ErrorState>
        </DealScreen>
      );
    }
    return (
      <div className="flex flex-col gap-4 pt-6">
        <BackLink to="/athlete/deals">Back to My Deals</BackLink>
        <ErrorState>This offer is no longer open for a counter.</ErrorState>
      </div>
    );
  }

  const { campaign, brand } = data;
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateRow = (idx, quantity) =>
    update({ deliverables: form.deliverables.map((r, i) => (i === idx ? { ...r, quantity: Math.max(0, Number(quantity) || 0) } : r)) });

  const send = async () => {
    if (!Number(form.compensation)) {
      toast.error("Enter your proposed compensation.");
      return;
    }
    if (!form.deliverables.some((r) => Number(r.quantity) > 0)) {
      toast.error("Keep at least one deliverable in the counter.");
      return;
    }
    setBusy(true);
    try {
      await respondToOffer(dealId, {
        action: "counter",
        compensation: Number(form.compensation),
        deliverables: form.deliverables.filter((r) => Number(r.quantity) > 0),
        startDate: form.startDate,
        endDate: form.endDate,
        milestones,
        exclusivityDays: Number(form.exclusivityDays) || 0,
        note: form.note.trim(),
      });
      toast.success(`Counter sent to ${brand.company}.`);
      refreshBadges();
      if (phone) toOffer();
      else navigate(`/athlete/deals/${dealId}/offer`);
    } catch (err) {
      toast.error(err.message || "Could not send the counter");
    } finally {
      setBusy(false);
    }
  };

  const offerUnits = (offer.deliverables || []).map((r) => `${r.quantity} ${r.platform} ${r.contentType}${r.quantity === 1 ? "" : "s"}`).join(", ");
  const summaryTone = (tone) => (tone === "green" ? "outline" : tone === "dark" ? "dark" : "passive");

  if (phone) {
    return (
      <CounterMobile
        onBack={toOffer}
        onCancel={toOffer}
        campaign={campaign}
        brand={brand}
        range={fmtLongRange(offer.startDate, offer.endDate)}
        reference={[
          ["Compensation", fmtMoney(offer.compensation, { cents: true })],
          ["Deliverables", offerUnits],
          ["Exclusivity", offer.exclusivityDays ? `${offer.exclusivityDays}-day category restriction` : "No exclusivity"],
          ["Timeline", fmtLongRange(offer.startDate, offer.endDate)],
          ["Payment schedule", scheduleText(offer.milestones || [])],
          ["Platform fees", "Paid by brand"],
        ]}
        form={form}
        update={update}
        updateRow={updateRow}
        schedules={SCHEDULES}
        scheduleHint={`Propose how payments should be split across milestones. Preview: ${scheduleText(milestones)}`}
        summaryRows={[
          ["Proposed compensation", fmtMoney(summary.compensation.value, { cents: true }), summary.compensation],
          ["Proposed exclusivity", summary.exclusivity.value, summary.exclusivity],
          ["Proposed deliverables", summary.deliverables.value, summary.deliverables],
        ]}
        analysis={analysis}
        busy={busy}
        onSend={send}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6 pt-6">
      <BackLink to={`/athlete/deals/${dealId}/offer`}>Back to offer</BackLink>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] bg-bm-mutedbg px-6 py-4">
        <span className="flex items-center gap-3">
          <span className="bm-h-s text-bm-ink">{campaign?.name}</span>
          <Badge tone="passive">Counter proposal</Badge>
        </span>
        <span className="bm-body-m text-bm-muted">
          {brand.company} · {fmtLongRange(offer.startDate, offer.endDate)}
        </span>
      </div>

      <div className="flex flex-col gap-[6px]">
        <Overline>Deal negotiation</Overline>
        <h1 className="bm-h-l text-bm-ink">Propose Counter Terms</h1>
        <p className="bm-body-l text-bm-muted">Propose your terms below. The brand will review and can accept, counter back, or decline.</p>
      </div>

      {/* Reference */}
      <Card padding="p-7" className="flex flex-col gap-4">
        <div>
          <Overline>Reference</Overline>
          <div className="bm-h-s text-bm-ink">Original Brand Offer</div>
        </div>
        <div className="grid gap-x-6 md:grid-cols-2">
          <div>
            <RefRow label="Compensation">{fmtMoney(offer.compensation, { cents: true })}</RefRow>
            <RefRow label="Deliverables">{offerUnits}</RefRow>
            <RefRow label="Exclusivity">
              {offer.exclusivityDays ? `${offer.exclusivityDays}-day category restriction` : "No exclusivity"}
            </RefRow>
          </div>
          <div>
            <RefRow label="Timeline">{fmtLongRange(offer.startDate, offer.endDate)}</RefRow>
            <RefRow label="Payment schedule">{scheduleText(offer.milestones || [])}</RefRow>
            <RefRow label="Platform fees">Paid by brand</RefRow>
          </div>
        </div>
      </Card>

      {/* Form */}
      <Card padding="p-7" className="flex flex-col gap-6">
        <div>
          <Overline>Negotiation form</Overline>
          <div className="bm-h-s text-bm-ink">Counter Proposal</div>
        </div>

        <Field label="Proposed compensation" hint="Enter your proposed total compensation. Cache fee applies to the brand." className="max-w-[360px]">
          <Input
            prefix="$"
            type="number"
            min="0"
            value={form.compensation}
            onChange={(e) => update({ compensation: e.target.value })}
          />
        </Field>

        <div className="flex flex-col gap-[6px]">
          <span className="bm-label-l text-bm-ink">Proposed deliverables</span>
          <div className="flex flex-wrap gap-3">
            {form.deliverables.map((row, idx) => (
              <Field key={`${row.platform}-${row.contentType}-${idx}`} label={`${row.platform} ${row.contentType}${row.contentType?.endsWith("s") ? "" : "s"}`} className="w-[180px]">
                <Input type="number" min="0" value={row.quantity} onChange={(e) => updateRow(idx, e.target.value)} />
              </Field>
            ))}
          </div>
          <span className="bm-body-s text-bm-muted">Adjust deliverable quantities. Adding deliverables may support a higher compensation ask.</span>
        </div>

        <div className="flex flex-col gap-[6px]">
          <span className="bm-label-l text-bm-ink">Proposed timeline</span>
          <div className="flex flex-wrap gap-3">
            <Input type="date" value={form.startDate} onChange={(e) => update({ startDate: e.target.value })} className="w-[200px]" />
            <span className="self-center bm-body-m text-bm-muted">to</span>
            <Input type="date" value={form.endDate} onChange={(e) => update({ endDate: e.target.value })} className="w-[200px]" />
          </div>
          <span className="bm-body-s text-bm-muted">Suggest a different campaign window if needed.</span>
        </div>

        <Field label="Proposed payment schedule" hint={`Propose how payments should be split across milestones. Preview: ${scheduleText(milestones)}`}>
          <Select value={form.schedule} onChange={(e) => update({ schedule: e.target.value })}>
            {SCHEDULES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Proposed exclusivity" hint="Shorter exclusivity windows give you more flexibility with other brands." className="max-w-[360px]">
          <span className="flex h-10 items-center gap-2 rounded-[8px] border border-bm-border bg-white px-[14px] focus-within:border-bm-orange">
            <input
              type="number"
              min="0"
              max="365"
              value={form.exclusivityDays}
              onChange={(e) => update({ exclusivityDays: e.target.value })}
              className="bm-bare min-w-0 flex-1 bg-transparent bm-body-m text-bm-ink outline-none"
            />
            <span className="bm-body-m text-bm-muted">days</span>
          </span>
        </Field>

        <Field label="Message to brand (optional)">
          <TextArea
            rows={4}
            value={form.note}
            onChange={(e) => update({ note: e.target.value })}
            placeholder={`Hi! Excited about ${campaign?.name || "this campaign"}. Here's why I'm proposing these terms...`}
          />
        </Field>
      </Card>

      {/* Summary */}
      <Card padding="p-7" className="flex flex-col gap-4">
        <div>
          <Overline>Summary of proposal</Overline>
          <div className="bm-h-s text-bm-ink">Counter Proposal Preview</div>
        </div>
        <div className="grid gap-6 sm:grid-cols-3">
          {[
            ["Proposed compensation", fmtMoney(summary.compensation.value, { cents: true }), summary.compensation],
            ["Proposed exclusivity", summary.exclusivity.value, summary.exclusivity],
            ["Proposed deliverables", summary.deliverables.value, summary.deliverables],
          ].map(([label, value, meta]) => (
            <div key={label} className="flex flex-col gap-[6px]">
              <span className="bm-body-s text-bm-muted">{label}</span>
              <span className="flex items-center gap-2">
                <span className="bm-h-m text-bm-ink">{value}</span>
                <Badge tone={summaryTone(meta.tone)} dot={meta.tone === "green" ? "#1f9d55" : undefined}>
                  {meta.chip}
                </Badge>
              </span>
            </div>
          ))}
        </div>
      </Card>

      <InsightBox title="Cache AI counter analysis">{analysis}</InsightBox>

      <ActionBar
        left={
          <>
            <Btn onClick={send} disabled={busy}>
              {busy ? "Sending..." : "Send counter"}
            </Btn>
            <button type="button" onClick={() => navigate(`/athlete/deals/${dealId}/offer`)} className="px-3 bm-label-l text-bm-ink hover:text-bm-muted">
              Cancel
            </button>
          </>
        }
      />
    </div>
  );
};

export default AthleteCounterPage;
